/**
 * @file Left-pane shell — Wash background, scroll container, max-width
 *       720px column with the design's reading padding.
 * @module @labcraft/lab/ui/components/chapter-pane
 *
 * Renders three regions inside the scroll container, top to bottom:
 *   - `head`: pinned to the top of the scroll via `position: sticky`
 *     so the chapter progress bar + header stay visible as the
 *     reader scrolls. Wash background to occlude content scrolling
 *     underneath.
 *   - `children`: the scrolling body (prose + `<LabStep>` cards +
 *     footer in the canonical composition).
 */

import type { JSX, ReactNode } from 'react'
import styles from './chapter_pane.module.css'

export type ChapterPaneProps = {
  /**
   * Sticky region pinned at the top of the scroll container. Use for
   * progress bar + chapter title — anything the reader should be
   * able to glance at without scrolling back to the top.
   */
  head?: ReactNode
  /** Scrolling body content. */
  children?: ReactNode
}

export function ChapterPane(props: ChapterPaneProps): JSX.Element {
  return (
    <section className={styles.pane} aria-label="Chapter content">
      <div className={styles.scroll}>
        {props.head !== undefined && (
          <div className={styles.head}>{props.head}</div>
        )}
        <div className={styles.body}>{props.children}</div>
      </div>
    </section>
  )
}
